/**
 * Verdi Chart Spec — v1
 *
 * A DashboardSpec is a complete render description for the data playground.
 * It owns everything the graph needs to render: which streams, how they look,
 * how they're grouped, what time window, and what thresholds to draw.
 *
 * What lives here:
 *   - Explicit stream IDs (always resolved before the spec is created/saved)
 *   - Display config per stream (color, label, axis, interpolation, group)
 *   - Panel layout and panel-level config
 *   - Shared time range
 *
 * What does NOT live here:
 *   - Query / discovery logic — that's getZoneSpec(), templates, the builder UI.
 *     Those are functions that *produce* a DashboardSpec; they're not part of the schema.
 *   - Fetch resolution (raw / 1m / 5m / 1h) — determined by the data layer based on
 *     the time range and viewport width. Not a render concern.
 *   - Builder UI state (expanded tree nodes, scroll position, modal open state) — ephemeral,
 *     lives in component state only.
 */

// ---------------------------------------------------------------------------
// Version
// ---------------------------------------------------------------------------

export const SPEC_VERSION = 1 as const;

// ---------------------------------------------------------------------------
// Time range
// ---------------------------------------------------------------------------

/**
 * Relative durations anchor the end to "now" and slide forward automatically.
 * This is the natural mode for live monitoring.
 * Absolute mode is for fixed historical windows (e.g. a saved investigation).
 */
export type RelativeDuration = "5m" | "1h" | "6h" | "1d" | "7d" | "4d" | "30d" | "90d" | "1y";

export type TimeRange =
    | { mode: "relative"; duration: RelativeDuration }
    | { mode: "absolute"; start: string; end: string }; // ISO 8601

// ---------------------------------------------------------------------------
// Axes
// ---------------------------------------------------------------------------

export enum AxisSide {
    Left = "left",
    Right = "right",
}

/**
 * Each panel declares its own axes. Multiple axes allow e.g. moisture on the
 * left and pressure on the right. Stream entries reference an axis by id.
 *
 * unitCategory maps to the unit system (e.g. "volumetricSoilMoisture", "liquidFlow",
 * "liquidPressure") and determines which unit preference is applied at render time.
 * It's a plain string, not the UnitCategory union: the value originates from the wire
 * and may be a category verdi-common doesn't yet enumerate. Render-time consumers narrow
 * it via isKnownUnitCategory and degrade gracefully (raw values) when it's unknown.
 */
export interface AxisSpec {
    id: string;
    unitCategory: string;
    side: AxisSide;
    label?: string;
}

// ---------------------------------------------------------------------------
// Groups
// ---------------------------------------------------------------------------

/**
 * How the group's members are combined and rendered.
 *
 * "basic"              — visual grouping only; members render individually (no computation)
 *
 * Aggregates ("result only" — member series hidden, derived series uses group color/axisId):
 * "sum"                — single derived line: sum of all member values at each timestamp
 * "average"            — single derived line: mean of all member values at each timestamp
 *
 * Aggregates ("members visible" — members still drawn alongside the derived series):
 * "stack"              — cumulative stacked area; top edge = sum. Only meaningful when
 *                        summing is physically valid (e.g. flow contributions to a zone total).
 *                        Member order follows streams[]. NOT for depth fans — use DepthOffsetPanelSpec.
 * "mean-with-shadows"  — mean line prominent; member lines rendered as grey shadows behind it.
 *
 * Symmetry: stack : sum  ::  mean-with-shadows : average
 *   (each aggregate has a "result only" and a "members visible" variant)
 */
export type GroupType = "basic" | "sum" | "average" | "stack" | "mean-with-shadows";

export interface GroupSpec {
    id: string;
    label?: string;
    type: GroupType;
    /** Color of the derived series ("sum" and "average" only). */
    color?: string;
    /** Axis the derived series is plotted on ("sum" and "average" only). References AxisSpec.id. */
    axisId?: string;
}

// ---------------------------------------------------------------------------
// Streams
// ---------------------------------------------------------------------------

/**
 * DATA fields (affect what is fetched — include in cache key):
 *   streamId
 *
 * VIEW fields (affect only rendering — changing these must NOT trigger a refetch):
 *   color, name, axisId, groupId
 *
 * The cache key is derived explicitly by the data layer, not inferred from the
 * spec shape. See getStreamCacheKey() in the data layer — it extracts streamId and
 * the active time range; everything else is ignored.
 */
export interface StreamEntry {
    /** DATA — drives fetching. Part of the cache key. */
    streamId: number;

    /** VIEW — hex color, assigned by generator or user. */
    color: string;
    /** VIEW — derived stream name from MasterIndex. */
    name: string;
    /** VIEW — references AxisSpec.id within the same panel. */
    axisId: string;
    /** VIEW — references GroupSpec.id within the same panel. */
    groupId?: string;
}

// ---------------------------------------------------------------------------
// Panels
// ---------------------------------------------------------------------------

export enum PanelType {
    TimeAxis = "time-axis",
}

/**
 * Shared base for panels that have a time axis.
 * These stack vertically and share synced horizontal scroll/zoom.
 */
export interface TemporalPanelBase {
    id: string;
    title?: string;
    streams: StreamEntry[];
    /**
     * Relative height weight among stacked panels (like CSS flex-grow).
     * Default 1. A panel with height 2 gets twice the vertical space.
     */
    height?: number;
}

/**
 * A panel with a time axis. Streams within the panel declare their own
 * seriesType ("line" | "bar"), allowing mixed charts — e.g. moisture as
 * lines and precipitation as bars on the same panel.
 */
export interface TimeAxisPanelSpec extends TemporalPanelBase {
    type: PanelType.TimeAxis;
    axes: AxisSpec[];
    groups?: GroupSpec[];
}

/** Additional panel types (depth-offset, gauge, reading) are planned and will widen this union. */
export type PanelSpec = TimeAxisPanelSpec;

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

export interface DashboardSpec {
    /** Bump this when making breaking schema changes. */
    version: typeof SPEC_VERSION;
    title?: string;
    description?: string;
    /**
     * Seeds the time range when this spec is first loaded. After that, the active
     * time range lives in session / URL state and is no longer the spec's concern.
     *
     * For link sharing, the recipient's time window comes from the URL params,
     * not this field.
     *
     * Omit to use the application default (e.g. last 7 days).
     */
    defaultTimeRange?: TimeRange;
    panels: PanelSpec[];
}
